import React, { useState } from "react";
import { PlayIcon } from "lucide-react";
type YouTubeEmbedProps = {
  youtubeId: string;
  title: string;
  className?: string;
  /** Small label shown on the poster before playback. */
  label?: string;
  size?: "lg" | "sm";
};

/**
 * Lightweight YouTube player: shows a poster image and only loads the
 * YouTube iframe after the visitor presses play (faster pages, fewer trackers).
 */
export function YouTubeEmbed({
  youtubeId,
  title,
  className = "aspect-video",
  label,
  size = "lg"
}: YouTubeEmbedProps) {
  const [playing, setPlaying] = useState(false);
  const [poster, setPoster] = useState(`https://i.ytimg.com/vi/${youtubeId}/maxresdefault.jpg`);
  function play() {
    setPlaying(true);
    window.dispatchEvent(new CustomEvent("mcc:conversion", {
      detail: {
        eventName: "video_play",
        youtubeId,
        title
      }
    }));
  }
  if (playing) {
    return <div className={`relative overflow-hidden bg-black ${className}`} data-id="element-453" data-mp="2qck91pso2go6">
        <iframe src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 h-full w-full" data-id="element-454" data-mp="2qeetl30106rq" />
      </div>;
  }
  return <button type="button" onClick={play} aria-label={`Play video: ${title}`} className={`group relative block w-full overflow-hidden bg-[#061f38] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bb1e35] focus-visible:ring-offset-2 ${className}`} data-id="element-455" data-mp="1udnxxvmntxo4">
      <img src={poster} alt="" loading="lazy" onError={() => setPoster(`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`)} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" data-id="element-456" data-mp="29b6kcrbv7yse" />
      <span className="absolute inset-0 bg-[#061f38]/30 transition-colors duration-300 group-hover:bg-[#061f38]/15" data-id="element-457" data-mp="iwxpe5urktrc" />
      <span className={`absolute left-1/2 top-1/2 grid -translate-x-1/2 -translate-y-1/2 place-items-center bg-[#c5233d] text-white transition-transform duration-200 ease-out group-hover:scale-105 ${size === "lg" ? "h-20 w-20" : "h-14 w-14"}`} data-id="element-458" data-mp="1m9egxvm8h6ov">
        <PlayIcon size={size === "lg" ? 28 : 20} fill="currentColor" data-id="element-459" data-mp="1r7pr67gfxj7m" />
      </span>
      {label && <span className="absolute bottom-0 left-0 bg-[#fbfaf6] px-4 py-2 text-left text-xs font-semibold tracking-[0.12em] text-[#061f38]" data-id="element-460" data-mp="1ra2e6z122jrj">{label}</span>}
    </button>;
}